import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  OwcButton,
  OwcIcon,
  OwcProgressBar,
  OwcSelectDropdown,
  OwcListItem,
  OwcTypography,
} from '@one/react';
import DashboardInsights from '../../../Common/DashboardsInsights/DashboardInsights';
import { useGroupData } from '../../../../contexts/GroupDataContext';
import {
  resolvePackageUrl,
  packageFileName,
  isSafeIgUri,
  isExternalIgLink,
} from '../../../../utils/fhirIg/fhirIg';
import styles from './IframePanel.module.css';

function IframePanel({
  igUri,
  igMeta,
  igLoading,
  igError,
  igVersions = [],
  igVersionsLoading = false,
  selectedIgVersion = '',
  onIgVersionChange,
  hasIgSelection = false,
}) {
  const { groupData } = useGroupData();
  const [frameLoading, setFrameLoading] = useState(false);
  const iframeRef = useRef(null);

  // Show the iframe's own load spinner whenever a new URL is requested.
  useEffect(() => {
    if (igUri) setFrameLoading(true);
  }, [igUri]);

  const title = igMeta?.name || '';
  const showVersionDropdown = hasIgSelection && (igVersionsLoading || igVersions.length > 0);

  // Downloadable package for the selected IG (right of the version selector).
  // The URL comes from the artifacts metadata; guard the scheme like the iframe
  // src since it is an untrusted API value.
  const rawPackageUrl = resolvePackageUrl(igMeta);
  const packageUrl = isSafeIgUri(rawPackageUrl) ? rawPackageUrl : '';
  const canDownloadPackage = hasIgSelection && !igLoading && !igError && !!packageUrl;

  // Open the same IG page shown in the iframe as a standalone browser tab.
  // Guard the scheme like the iframe src since igUri is an untrusted API value.
  const canOpenInBrowser = hasIgSelection && !igLoading && !igError && isSafeIgUri(igUri);

  const handleOpenInBrowser = () => {
    if (!isSafeIgUri(igUri)) return;
    window.open(igUri, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadPackage = () => {
    if (!packageUrl) return;
    const link = document.createElement('a');
    link.href = packageUrl;
    link.download = packageFileName(igMeta, packageUrl);
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFrameLoad = useCallback(() => {
    setFrameLoading(false);

    const frame = iframeRef.current;
    let doc;
    try {
      doc = frame && frame.contentDocument;
    } catch {
      // Cross-origin (unexpected here): nothing we can safely do.
      doc = null;
    }
    if (!doc) return;

    const onClick = (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return; // respect middle-click / open-in-new-tab shortcuts
      }
      const anchorEl = event.target && event.target.closest && event.target.closest('a[href]');
      if (!anchorEl) return;
      // Already targets a new tab: let the browser handle it natively.
      if ((anchorEl.getAttribute('target') || '').toLowerCase() === '_blank') return;

      const rawHref = anchorEl.getAttribute('href');
      if (!isExternalIgLink(rawHref, anchorEl.href, window.location.origin)) return;

      // External link: open in a new outer tab, keep the iframe where it is.
      event.preventDefault();
      window.open(anchorEl.href, '_blank', 'noopener,noreferrer');
    };

    doc.addEventListener('click', onClick);
    // Best-effort cleanup: the listener dies with the document on the next
    // load/navigation, but detach explicitly when the frame unloads too.
    const detach = () => doc.removeEventListener('click', onClick);
    doc.defaultView && doc.defaultView.addEventListener('unload', detach, { once: true });
  }, []);

  // Before any IG is picked, reuse the shared dashboard empty state.
  if (!hasIgSelection && !igLoading && !igError) {
    return <DashboardInsights groupData={groupData} />;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.titleWrap}>
          {title && (
            <OwcTypography className={styles.title} title={title}>
              {title}
            </OwcTypography>
          )}
        </div>

        {(showVersionDropdown || canOpenInBrowser || canDownloadPackage) && (
          <div className={styles.headerActions}>
            {showVersionDropdown && (
              <OwcSelectDropdown
                size="small"
                label="Version"
                placeholder={igVersionsLoading ? 'Loading versions...' : 'Version'}
                value={selectedIgVersion || igVersions[0] || ''}
                onValueChange={onIgVersionChange}
                disabled={igVersionsLoading || igVersions.length === 0}
                no-clear-icon
                variant="outlined"
                className={styles.versionDropdown}
                aria-label="Implementation guide version"
              >
                {igVersions.map((version) => (
                  <OwcListItem key={version} value={version}>
                    {version}
                  </OwcListItem>
                ))}
              </OwcSelectDropdown>
            )}

            {canOpenInBrowser && (
              <OwcButton
                variant="primary"
                onClick={handleOpenInBrowser}
                className={styles.openInBrowserButton}
                aria-label="Open implementation guide in a new browser tab"
              >
                Open in browser
                <OwcIcon
                  name="external_link"
                  family="outlined"
                  className={styles.openInBrowserIcon}
                />
              </OwcButton>
            )}

            {canDownloadPackage && (
              <OwcButton
                variant="primary"
                onClick={handleDownloadPackage}
                className={styles.downloadButton}
                aria-label="Download implementation guide package"
              >
                Download {title}
                <OwcIcon
                  name="download"
                  family="outlined"
                  style={{ paddingLeft: '4px', color: '#fff' }}
                />
              </OwcButton>
            )}
          </div>
        )}
      </div>

      <div className={styles.frameArea}>
        {igLoading && (
          <div className={styles.overlay} role="status" aria-label="Loading implementation guide">
            <OwcProgressBar
              caption="Loading implementation guide..."
              value={undefined}
              style={{ width: '320px' }}
            />
          </div>
        )}

        {igError && !igLoading && (
          <div className={styles.errorState} role="alert">
            <owc-banner status="error">{igError}</owc-banner>
          </div>
        )}

        {!igError && igUri && (
          <>
            {frameLoading && !igLoading && (
              <div className={styles.overlay} role="status" aria-label="Loading page">
                <OwcProgressBar value={undefined} style={{ width: '240px' }} />
              </div>
            )}
            <iframe
              key={igUri}
              ref={iframeRef}
              data-testid="ig-frame"
              className={styles.frame}
              src={igUri}
              title={title || 'Implementation guide'}
              onLoad={handleFrameLoad}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(IframePanel);
